import React, {useMemo, useState} from "react";
import styled from "styled-components";
import {ReportTotalCards} from "./ReportSharedLayout";
import {KPI_CHART_COLORS} from "../../common/ChartColors";

const toNumber = value => {
  const normalized = typeof value === "string" ? value.replace(/,/g, "") : value;
  const number = Number(normalized ?? 0);
  return Number.isFinite(number) ? number : 0;
};

const getLabel = (item, index, labelKey) => item?.[labelKey] ?? item?.campaignName ?? item?.adverName ??
    item?.statisticsDate ?? item?.statisticsStartDate ?? `데이터 ${index + 1}`;

export default function ReportOverviewChart({data = [], title = "주요 성과 비교", labelKey, titleSize = 14, hideHeaderBorder = false}) {
  const [visibleMetrics, setVisibleMetrics] = useState({exposureCount: true, costAmount: true});
  const toggleMetric = id => setVisibleMetrics(current => {
    const selectedCount = Object.values(current).filter(Boolean).length;
    if (current[id] && selectedCount === 1) return current;
    return {...current, [id]: !current[id]};
  });
  const items = useMemo(() => {
    const mappedItems = (Array.isArray(data) ? data : [])
      .map((item, index) => ({item, label: getLabel(item, index, labelKey), exposureCount: toNumber(item?.exposureCount), costAmount: toNumber(item?.costAmount)}));
    const chartItems = labelKey ? Array.from(mappedItems.reduce((result, current) => {
      const previous = result.get(current.label);
      result.set(current.label, previous ? {
        ...previous,
        exposureCount: previous.exposureCount + current.exposureCount,
        costAmount: previous.costAmount + current.costAmount
      } : current);
      return result;
    }, new Map()).values()) : mappedItems;

    return chartItems
      .sort((first, second) => second.exposureCount - first.exposureCount)
      .slice(0, 5);
  }, [data, labelKey]);
  const maxExposure = Math.max(...items.map(item => item.exposureCount), 1);
  const maxCost = Math.max(...items.map(item => item.costAmount), 1);
  const totalSummary = useMemo(() => (Array.isArray(data) ? data : []).reduce((result, item) => ({
    exposureCount: result.exposureCount + toNumber(item?.exposureCount),
    costAmount: result.costAmount + toNumber(item?.costAmount)
  }), {exposureCount: 0, costAmount: 0}), [data]);

  return <ChartCard><ChartHeader $hideBorder={hideHeaderBorder}><div><ChartTitle $titleSize={titleSize}>{title}</ChartTitle><ChartDescription>확인할 지표를 선택하면 차트에 바로 반영됩니다.</ChartDescription></div>
    <ReportTotalCards exposureCount={totalSummary.exposureCount} costAmount={totalSummary.costAmount} compact/>
  </ChartHeader><MetricOptions>
    <MetricButton type="button" $active={visibleMetrics.exposureCount} onClick={() => toggleMetric('exposureCount')}><MetricDot $active={visibleMetrics.exposureCount} $color={KPI_CHART_COLORS.exposureCount}/>노출수</MetricButton>
    <MetricButton type="button" $active={visibleMetrics.costAmount} onClick={() => toggleMetric('costAmount')}><MetricDot $active={visibleMetrics.costAmount} $color={KPI_CHART_COLORS.costAmount}/>비용</MetricButton>
  </MetricOptions>
    {items.length === 0 ? <EmptyChart>조회된 데이터가 없습니다.</EmptyChart> : <ChartBody>{items.map(({item, label, exposureCount, costAmount}, index) =>
      <ChartRow key={item?.campaignId ?? item?.userId ?? `${label}-${index}`}><ChartLabel title={label}>{label}</ChartLabel><Bars>
        {visibleMetrics.exposureCount && <BarLine><BarName>노출</BarName><Track><Bar $exposure $width={(exposureCount / maxExposure) * 100}/></Track><Value>{exposureCount.toLocaleString()}</Value></BarLine>}
        {visibleMetrics.costAmount && <BarLine><BarName>비용</BarName><Track><Bar $width={(costAmount / maxCost) * 100}/></Track><Value>{costAmount.toLocaleString()}</Value></BarLine>}
      </Bars></ChartRow>)}</ChartBody>}
  </ChartCard>;
}

const ChartCard = styled.section`margin:0;overflow:hidden;border:1px solid #e5e8eb;border-radius:10px;background:#fff;box-shadow:0 5px 18px rgba(31,41,55,.045);`;
const ChartHeader = styled.header`
  display:flex;
  min-height:64px;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  padding:15px 20px;
  border-bottom:${({$hideBorder}) => $hideBorder ? "0" : "1px solid #e8ebee"};
  background:#fff;

  @media(max-width:900px){
    align-items:flex-start;
    flex-direction:column;
    width:100%;
  }
`;
const ChartTitle = styled.h3`margin:0 0 5px;color:#2f363d;font-size:${({$titleSize}) => `${$titleSize}px`};font-weight:800;`;
const ChartDescription = styled.p`margin:0;color:#8a9299;font-size:10px;`;
const MetricOptions = styled.div`display:flex;align-items:center;justify-content:flex-start;gap:8px;padding:14px 20px 2px;`;
const MetricButton = styled.button`display:inline-flex;height:30px;align-items:center;gap:7px;padding:0 8px;border:0;border-radius:5px;background:transparent;color:#444;font-size:11px;font-weight:600;cursor:pointer;opacity:${({$active})=>$active?1:.4};transition:background-color .15s ease,opacity .15s ease;&:hover{background:#f7f8f9;opacity:1;}&:focus-visible{outline:1px solid #f5811f;outline-offset:2px;}`;
const MetricDot = styled.span`display:inline-block;width:8px;height:8px;flex:0 0 8px;box-sizing:border-box;border:2px solid ${({$color})=>$color};border-radius:2px;background:${({$active,$color})=>$active?$color:'#fff'};`;
const ChartBody = styled.div`display:flex;flex-direction:column;gap:14px;padding:8px 20px 18px;`;
const ChartRow = styled.div`display:grid;grid-template-columns:minmax(110px,20%) minmax(0,1fr);align-items:center;gap:14px;`;
const ChartLabel = styled.strong`overflow:hidden;color:#4b535b;font-size:10px;text-overflow:ellipsis;white-space:nowrap;`;
const Bars = styled.div`display:flex;min-width:0;flex-direction:column;gap:6px;`;
const BarLine = styled.div`display:grid;grid-template-columns:28px minmax(0,1fr) 70px;align-items:center;gap:7px;`;
const BarName = styled.span`color:#91989f;font-size:9px;`;
const Track = styled.span`display:block;width:100%;height:7px;overflow:hidden;border-radius:999px;background:#f0f2f4;`;
const Bar = styled.span`display:block;width:${({$width}) => `${Math.max(2, Number($width) || 0)}%`};height:100%;border-radius:inherit;background:${({$exposure}) => $exposure ? KPI_CHART_COLORS.exposureCount : KPI_CHART_COLORS.costAmount};`;
const Value = styled.span`color:#555e66;font-size:9px;font-weight:700;text-align:right;`;
const EmptyChart = styled.div`display:flex;min-height:180px;align-items:center;justify-content:center;color:#969da4;font-size:11px;`;
